import {
  Alert,
  AUTH_JWT_TOKEN,
  AUTH_JWT_TOKEN_EXPIRATION,
  AUTH_REFRESH_TOKEN,
  AUTH_REFRESH_TOKEN_EXPIRATION,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  InfoBanner,
  Link,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { AuthRequestV2, AuthResult, AuthResultType, AuthViewResource } from "generated/openApi";
import { AuthField } from "pages/auth/components/AuthField";
import { AuthPageWrapper } from "pages/auth/components/AuthPageWrapper";
import { authColors, pillButtonStyles } from "pages/auth/styles/authStyles";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppAuthStatus } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { forgotCredentialPageV2Route } from "./ForgotCredentialPageV2";
import { MicrosoftSssoButton } from "pages/auth/components/MicrosoftSssoButton";
import { tenantListPageRoute } from "./TenantListPage";

const routeName = "login";

class OptionalParams {
  redirectTo?: string = "";
}

export const loginPageV2Route: PageRoute<undefined, OptionalParams> = {
  handle: "Login",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
  getOptionalParams: (params: OptionalParams) => buildOptionalParams(params),
};

export const LoginPageV2 = () => {
  const { optionalParams } = useParams(routeName, undefined, OptionalParams);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loginModel, setLoginModel] = useState<AuthRequestV2>({} as AuthRequestV2);
  const [viewResource, setViewResource] = useState<AuthViewResource | null>(null);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [isMigrating, setIsMigrating] = useState<boolean>(false);

  const properties = viewResource?.properties || [];

  const handleFieldChange = (name: string, value: string) => {
    setLoginModel((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    ApiService.authV2.getAuthViewResource().then(setViewResource);
  }, []);

  const handleMicrosoftSsoLogin = async () => {
    const baseUrl = process.env.REACT_APP_API_ENDPOINT;
    // Can't use any wrapper around this API call or we get a CORS error from Microsoft..
    window.location.href = `${baseUrl}/AuthV2/MicrosoftLogin`;

    // Can't use this..
    // var response = await ApiService.authV2.microsoftLogin();
  };

  const authenticate = () => {
    setValidationErrors({});

    ApiService.authV2
      .authenticate(loginModel)
      .then((response: AuthResult) => {
        if (response.authResultType === AuthResultType.Authenticated) {
          localStorage.setItem(AUTH_JWT_TOKEN, response.jwtToken!);
          localStorage.setItem(AUTH_JWT_TOKEN_EXPIRATION, response.jwtTokenExpiration?.toString()!);
          localStorage.setItem(AUTH_REFRESH_TOKEN, response.refreshToken!);
          localStorage.setItem(AUTH_REFRESH_TOKEN_EXPIRATION, response.refreshTokenExpiration?.toString()!);

          dispatch(setAppAuthStatus("AUTHENTICATED"));

          // If only one tenant, just take them to home page, otherwise they select tenants
          if (!response.multipleGrants) {
            navigate("/");
            window.location.reload();
          }
          else {
            navigate(optionalParams.redirectTo || `/${tenantListPageRoute.getPath()}`);
          }
        } else if (response.authResultType === AuthResultType.PasswordExpired) {
          setLoginModel((prev) => ({ ...prev, isChangingPassword: true }));
        } else if (response.authResultType === AuthResultType.UserMigration) {
          setIsMigrating(true);
        }
      })
      .catch((ex) => setValidationErrors(ex.data));
  };

  const generalErrors = validationErrors?.[pn<AuthRequestV2>("generalValidationErrors")];

  return (
    <AuthPageWrapper>
      <Card sx={{ maxWidth: "500px", width: "100%" }}>
        <Form>
          <Stack px={3} py={7} spacing="lg">
            <Stack spacing="sm" alignItems="center">
              <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                Sign In
              </Typography>
              <Typography variant="subtitle2">
                Fill in the fields below to sign into your account.
              </Typography>
            </Stack>

            <Stack spacing="md">
              {generalErrors && <Alert severity="error">{generalErrors[0]}</Alert>}

              {loginModel.isChangingPassword && (
                <Alert severity="warning">
                  Your password has expired. Please enter new passwords for your account.
                </Alert>
              )}
              {isMigrating && (<InfoBanner title="Important Notice">
                <Typography variant="caption" color="primary">We've made some updates to our login process. To finish setting up your account, please check your email and follow the one-time setup link sent to: </Typography>
                <Typography variant="h6">{loginModel.email}</Typography>
              </InfoBanner>)}

              <AuthField
                name="email"
                value={loginModel.email || ""}
                onChange={handleFieldChange}
                properties={properties}
                validationErrors={validationErrors}
                disabled={loginModel.isChangingPassword || isMigrating}
              />
              <AuthField
                name="password"
                value={loginModel.password || ""}
                onChange={handleFieldChange}
                properties={properties}
                validationErrors={validationErrors}
                type="password"
                disabled={loginModel.isChangingPassword || isMigrating}
              />

              {loginModel.isChangingPassword && (
                <>
                  <AuthField
                    name="newPassword"
                    value={loginModel.newPassword || ""}
                    onChange={handleFieldChange}
                    properties={properties}
                    validationErrors={validationErrors}
                    type="password"
                  />
                  <AuthField
                    name="confirmNewPassword"
                    value={loginModel.confirmNewPassword || ""}
                    onChange={handleFieldChange}
                    properties={properties}
                    validationErrors={validationErrors}
                    type="password"
                  />
                </>
              )}

              <SubmitButton
                onClick={authenticate}
                label="Login"
                fullWidth
                sx={{ height: "50px", ...pillButtonStyles }}
              />

              <Stack spacing="sm" alignItems="center">
                <Typography variant="subtitle1">
                  Or log in with:
                </Typography>
              </Stack>

              <MicrosoftSssoButton
                onClick={handleMicrosoftSsoLogin}
              />

              <Stack direction="row">
                <Link
                  ml="auto"
                  underline="hover"
                  sx={{
                    color: authColors.primary.light,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": { color: authColors.primary.main },
                  }}
                  onClick={() =>
                    navigate({
                      pathname: `/${forgotCredentialPageV2Route.getPath()}`
                    })
                  }
                >
                  Forgot Password
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Form>
      </Card>
    </AuthPageWrapper>
  );
};